import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseValue } from 'react-native-controlled-mentions';

import ChatCard from '@components/Chat/ChatCard.web';
import { Box, Text } from '@components/Restyle';
import GlobalSearchList from '@components/Search/GlobalSearchList';
import { ChatRowSkeleton } from '@components/shared/skeletons/ChatRowSkeleton';
import {
  ChatDrawerScreen,
  useNavigateToChatDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import { Chat, Message, SearchableResult } from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useSearch from '@hooks/useSearch';
import { Search } from '@root/types';
import {
  CHAT_RESULTS_PREVIEW_COUNT,
  toChatFromSearchResult,
} from '@screens/Global/chatSearchResults';
import { triggersConfigPlain } from '@utils/mentions';

const itemSeparatorComponent = (
  <Box borderBottomWidth={1} borderColor='grey01' marginHorizontal='m' />
);

interface IMessageSearch {
  search: string;
  data: Search<Message>[];
  chats?: Search<Chat>[];
  loading: boolean;
  onPress: () => void;
  sectionFooter?: any;
  showFooter?: boolean;
  hasError?: boolean;
}
const ChatsSearch: React.FC<IMessageSearch> = ({
  search,
  data: allData,
  chats = [],
  loading,
  onPress,
  sectionFooter,
  showFooter,
  hasError = false,
}) => {
  const { t } = useTranslation('models');
  const navigation = useNavigateToChatDrawer();
  const { setActiveMessageId, setIsOpenChat, setScrollRequested } =
    useActiveChat();

  const { setIsSearchPreview, unavailableMessageIds } = useSearch();
  // TA-11078: a result whose message was deleted after indexing is dropped
  // once opening it has failed, so it can't be clicked again.
  const data = allData.filter(
    (item) => !unavailableMessageIds.includes(item.record.id)
  );

  // Collapsed to the first few chats until "View all"; a new term collapses
  // it again.
  const [showAllChats, setShowAllChats] = useState(false);
  useEffect(() => {
    setShowAllChats(false);
  }, [search]);

  const sectionTitle = (title: string) => (
    <Text variant='labelEmphasized'>{title}</Text>
  );

  // Hidden while empty so a messages-only result looks exactly as before.
  const chatsSection =
    chats.length > 0
      ? [
          {
            title: t('shared:chats'),
            headerLeft: sectionTitle(t('shared:chats')),
            data: showAllChats
              ? chats
              : chats.slice(0, CHAT_RESULTS_PREVIEW_COUNT),
            totalCount: chats.length,
            onViewAll: showAllChats ? undefined : () => setShowAllChats(true),
            render: (item: Search<SearchableResult>) => {
              const chat = toChatFromSearchResult(item as Search<Chat>);
              return (
                <ChatCard
                  filterVal={search}
                  chatCard={chat}
                  isEditing={false}
                  isFromChatSearch={true}
                  onPress={() => {
                    onPress?.();
                    setActiveMessageId?.(undefined);
                    setIsOpenChat(true);
                    setIsSearchPreview(false);
                    navigation({
                      screen: ChatDrawerScreen.details,
                      activeChat: chat,
                    });
                  }}
                />
              );
            },
            itemSeparatorComponent,
          },
        ]
      : [];

  return (
    <GlobalSearchList
      loading={loading}
      onPress={() => {}}
      isViewAllContacts={false}
      isChatsSearch
      search={search}
      hasError={hasError}
      // TA-10279: adopts the `skeletonRow` prop GlobalSearchList already
      // exposed for TA-10278 — Chats search was the one caller deliberately
      // left on the spinner fallback at that time.
      skeletonRow={() => <ChatRowSkeleton />}
      lists={[
        ...chatsSection,
        {
          title: 'Chat history',
          // Only labelled when there's a Chats section above to tell apart.
          headerLeft:
            chatsSection.length > 0 && data.length > 0
              ? sectionTitle(t('shared:chatHistory'))
              : undefined,
          data: data,
          totalCount: data.length,
          render: (item) => {
            const record = item.record as Message;
            const { chat: initialChat, id: messageId } = record;

            const highlightAuthorName = item?.highlight?.authorName;
            const authorName = record.authorName;

            let messageAuthorName =
              initialChat.name === authorName
                ? ''
                : `${highlightAuthorName || authorName}: `;
            let messageBody = '';
            const message = item?.highlight?.body?.[0] || record?.body;

            const { parts } = parseValue(message || '', [
              triggersConfigPlain.mention,
            ]);
            {
              parts.map((item) => {
                messageBody += item.text;
              });
            }
            const chat: Chat = {
              ...initialChat,
              messageBody: `${
                record.isSender
                  ? t('models:chat.isSender')
                  : record.author?.name
              }: ${record.body}`,
              recentActivityAt: record.publishedAt,
              users: [record?.chat?.users],
            };

            // Gate on the Documents, not on attachments.length: PublicTeam
            // joined the Attachment union, and a body-less message carrying
            // only one would otherwise render "0 files".
            const documents = (record.attachments ?? []).filter(
              (i) => i.__typename === 'Document'
            );
            if (messageBody?.trim() === '' && documents.length > 0) {
              if (documents.some((i) => 'isImage' in i && i.isImage)) {
                chat.messageIcon = 'Gallery';
                messageBody = t('chat.labels.photo', {
                  count: documents.length,
                });
              } else {
                chat.messageIcon = 'Paperclip';
                messageBody = t('chat.labels.file', {
                  count: documents.length,
                });
              }
              messageAuthorName = '';
            }
            const highlightedBody = `${messageAuthorName}${messageBody}`;
            return (
              <ChatCard
                messageData={[record]}
                filterVal={search}
                chatCard={chat}
                isEditing={false}
                isFromChatSearch={true}
                highlightedBody={highlightedBody}
                onPress={() => {
                  onPress?.();
                  setScrollRequested(true);
                  setActiveMessageId?.(undefined);
                  setIsOpenChat(true);
                  setIsSearchPreview(true);
                  navigation({
                    screen: ChatDrawerScreen.details,
                    activeChat: chat,
                    activeMessageId: messageId,
                  });
                }}
              />
            );
          },
          itemSeparatorComponent,
          sectionFooter: showFooter ? sectionFooter : <Box mb='xs'></Box>,
        },
      ]}
    />
  );
};

export default ChatsSearch;
